import { loadStripe, Stripe } from '@stripe/stripe-js';
import { STRIPE_CONFIG, SUBSCRIPTION_PLANS } from '../config/stripe';
import { User } from 'firebase/auth';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from './firebase/config';

class PaymentService {
  private stripe: Promise<Stripe | null>;

  constructor() {
    this.stripe = loadStripe(STRIPE_CONFIG.publishableKey);
  }

  /**
   * Create a checkout session for subscription
   * Note: In production, this should be done on the backend
   */
  async createCheckoutSession(
    user: User,
    priceId: string,
    successUrl?: string,
    cancelUrl?: string
  ) {
    try {
      // In production, this should be an API call to your backend
      // For now, we'll simulate the checkout process
      const response = await this.simulateBackendCheckout(user, priceId, successUrl, cancelUrl);
      return response;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      throw error;
    }
  }

  /**
   * Simulate backend checkout creation
   * In production, replace this with actual API call to your backend
   */
  private async simulateBackendCheckout(
    user: User,
    priceId: string,
    successUrl?: string,
    cancelUrl?: string
  ) {
    // This would normally be done on your backend using the Stripe secret key
    // For demonstration, we'll create a client-side checkout
    
    const stripe = await this.stripe;
    if (!stripe) throw new Error('Stripe not initialized');

    // In production, you would:
    // 1. Call your backend API endpoint
    // 2. Backend creates checkout session with Stripe API
    // 3. Return session ID to frontend
    // 4. Redirect to Stripe Checkout
    
    // For now, return a mock session
    return {
      sessionId: 'mock_session_' + Date.now(),
      url: `https://checkout.stripe.com/c/pay/mock_session#${priceId}`,
      successUrl: successUrl || STRIPE_CONFIG.urls.success,
      cancelUrl: cancelUrl || STRIPE_CONFIG.urls.cancel,
    };
  }

  /**
   * Process subscription with Stripe Checkout
   */
  async processSubscription(
    user: User,
    planType: 'pro_monthly' | 'pro_yearly'
  ): Promise<void> {
    try {
      const plan = SUBSCRIPTION_PLANS[planType];
      if (!plan || !plan.priceId) {
        throw new Error('Invalid subscription plan');
      }

      const stripe = await this.stripe;
      if (!stripe) throw new Error('Stripe not initialized');

      // Create checkout session parameters
      const checkoutParams = {
        lineItems: [
          {
            price: plan.priceId,
            quantity: 1,
          },
        ],
        mode: 'subscription' as const,
        successUrl: `${window.location.origin}/payment-success?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${window.location.origin}/payment-cancel`,
        customerEmail: user.email || undefined,
        clientReferenceId: user.uid,
        metadata: {
          userId: user.uid,
          planType: planType,
        },
      };

      // Try to use backend API first (for production)
      // Use relative URL for API endpoints (works with Vercel)
      
      try {
        // First, try to create session via backend API
        const response = await fetch('/api/create-checkout-session', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            priceId: plan.priceId,
            userId: user.uid,
            userEmail: user.email,
            planType: planType,
            successUrl: checkoutParams.successUrl,
            cancelUrl: checkoutParams.cancelUrl,
          }),
        });

        if (response.ok) {
          const { sessionId, sessionUrl } = await response.json();
          
          if (sessionUrl) {
            // Redirect to Stripe Checkout using the URL from backend
            window.location.href = sessionUrl;
            return;
          } else if (sessionId) {
            // Use session ID to redirect
            const { error } = await stripe.redirectToCheckout({ sessionId });
            if (error) {
              console.error('Stripe redirect error:', error);
              throw error;
            }
            return;
          }
        }
      } catch (err) {
        console.log('Backend API not available, trying direct checkout...', err);
      }

      // If backend API is not available, show error message
      console.error('Backend API not available. Stripe Checkout requires server-side session creation.');
      alert('Payment processing is temporarily unavailable. Please try again later or contact support.');
    } catch (error) {
      console.error('Error processing subscription:', error);
      throw error;
    }
  }

  /**
   * Mock checkout redirect for demonstration
   * Replace with actual Stripe Checkout redirect in production
   */
  private mockCheckoutRedirect(user: User, planType: string) {
    // In production, you would redirect to Stripe Checkout
    // For now, we'll show an alert with payment details
    
    const plan = SUBSCRIPTION_PLANS[planType as keyof typeof SUBSCRIPTION_PLANS];
    
    alert(`
      Stripe Checkout (Demo Mode)
      ============================
      Plan: ${plan.name}
      Price: $${plan.price}/${plan.interval || 'one-time'}
      User: ${user.email}
      
      In production, this would redirect to:
      https://checkout.stripe.com/...
      
      To complete integration:
      1. Create products in Stripe Dashboard
      2. Set up webhook endpoints
      3. Create backend API for checkout
      4. Handle payment success/failure
    `);

    // Simulate successful payment for demo
    setTimeout(() => {
      this.handlePaymentSuccess(user, planType as 'pro_monthly' | 'pro_yearly');
    }, 2000);
  }

  /**
   * Handle successful payment
   */
  async handlePaymentSuccess(
    user: User,
    planType: 'pro_monthly' | 'pro_yearly'
  ): Promise<void> {
    try {
      // Update user's subscription in Firebase
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        subscription: 'pro',
        subscriptionPlan: planType,
        subscriptionStartDate: new Date(),
        subscriptionStatus: 'active',
        updatedAt: new Date()
      });

      // Show success message
      console.log('Subscription activated successfully!');
    } catch (error) {
      console.error('Error updating subscription:', error);
      throw error;
    }
  }

  /**
   * Cancel subscription
   */
  async cancelSubscription(user: User): Promise<void> {
    try {
      // In production, call your backend to cancel Stripe subscription
      // Backend should:
      // 1. Cancel subscription in Stripe
      // 2. Update database
      // 3. Return confirmation
      
      // For demo, update Firebase directly
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        subscription: 'free',
        subscriptionPlan: null,
        subscriptionStatus: 'cancelled',
        subscriptionEndDate: new Date(),
        updatedAt: new Date()
      });

      console.log('Subscription cancelled');
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      throw error;
    }
  }

  /**
   * Get customer portal URL for managing subscription
   */
  async getCustomerPortalUrl(user: User): Promise<string> {
    try {
      // In production, call your backend to create portal session
      // Backend should:
      // 1. Create billing portal session with Stripe
      // 2. Return portal URL
      
      // For demo, return mock URL
      return `https://billing.stripe.com/p/session/mock_${user.uid}`;
    } catch (error) {
      console.error('Error getting customer portal URL:', error);
      throw error;
    }
  }

  /**
   * Verify payment status
   */
  async verifyPaymentStatus(sessionId: string): Promise<boolean> {
    try {
      // In production, call your backend to verify payment with Stripe
      // Backend should:
      // 1. Retrieve checkout session from Stripe
      // 2. Verify payment status
      // 3. Update database if successful
      
      // For demo, return true
      return true;
    } catch (error) {
      console.error('Error verifying payment:', error);
      return false;
    }
  }
}

export const paymentService = new PaymentService();
export default paymentService;